import React, { useEffect, useState } from "react";
import { Sparkles, Wrench, Bug, Bell, Megaphone, ArrowRight } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Auto-shown at dashboard root — checks the server for any What's New
// announcements targeted at the current user AND not yet dismissed by
// them. Renders one popup card per announcement and lets the user
// step through with "Got it" (dismisses the current one).
//
// Uses the dashboard WebSocket to catch `whats_new.published` frames
// so a note authored while a session is already open lights up
// instantly — no reload needed.

const CATEGORY_META = {
  new_feature:  { icon: Sparkles, label: "New feature",  tone: "border-accent/40 text-accent" },
  improvement:  { icon: Wrench,   label: "Improvement",  tone: "border-emerald-500/40 text-emerald-700 dark:text-emerald-300" },
  bug_fix:      { icon: Bug,      label: "Bug fix",      tone: "border-amber-500/40 text-amber-700 dark:text-amber-300" },
  announcement: { icon: Bell,     label: "Announcement", tone: "border-sky-500/40 text-sky-700 dark:text-sky-300" },
};

export default function WhatsNewInbox() {
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);

  const fetchQueue = async () => {
    try {
      const { data } = await api.get("/admin/whats-new/my");
      setQueue(data || []);
      setIndex(0);
    } catch (e) {
      // Silent: this is a passive background check — a 401 or offline
      // state shouldn't spam the toaster.
    }
  };

  useEffect(() => {
    fetchQueue();
    // Re-check on focus so a session left open overnight picks up any
    // notes published while it was away.
    const onFocus = () => fetchQueue();
    window.addEventListener("focus", onFocus);
    // Live-hook to catch notes published while the tab stays open.
    const bus = window;
    const onLive = (e) => {
      if (e?.detail?.type === "whats_new.published") fetchQueue();
    };
    bus.addEventListener("yspa:ws-broadcast", onLive);
    return () => {
      window.removeEventListener("focus", onFocus);
      bus.removeEventListener("yspa:ws-broadcast", onLive);
    };
  }, []);

  if (queue.length === 0 || index >= queue.length) return null;
  const note = queue[index];
  const cats = (note.categories || []).map((c) => CATEGORY_META[c]).filter(Boolean);

  const gotIt = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/whats-new/${note.id}/dismiss`);
      // Slide to the next note in the queue. Once we've dismissed the
      // last one the dialog closes on its own (index >= length).
      setIndex((i) => i + 1);
    } catch (e) {
      // If dismiss fails, at least close the current one locally.
      setIndex((i) => i + 1);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={() => { /* forced flow — dismiss button only */ }}>
      <DialogContent
        className="max-w-lg"
        // Suppress the built-in close button so the crew must tap
        // "Got it" — that's the only path that actually stamps
        // `dismissed_by` and stops the popup returning next load.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="whatsnew-inbox"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
              <Megaphone className="h-5 w-5 text-accent" strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-accent">What&apos;s new</div>
              <DialogTitle className="font-display text-2xl truncate">{note.title}</DialogTitle>
            </div>
          </div>
        </DialogHeader>

        {cats.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {cats.map((c, i) => {
              const Icon = c.icon;
              return (
                <Badge key={i} variant="outline" className={`text-[10px] ${c.tone}`}>
                  <Icon className="inline h-2.5 w-2.5 mr-1" />{c.label}
                </Badge>
              );
            })}
          </div>
        )}

        {note.body && (
          <div className="text-sm text-foreground/85 whitespace-pre-wrap leading-relaxed" data-testid="whatsnew-inbox-body">
            {note.body}
          </div>
        )}

        {note.created_by_name && (
          <div className="text-[11px] text-muted-foreground pt-2 border-t border-border">
            From {note.created_by_name}
            {note.created_at && ` · ${new Date(note.created_at).toLocaleString(undefined, { month: "short", day: "numeric" })}`}
          </div>
        )}

        <DialogFooter className="pt-2">
          {queue.length > 1 && (
            <div className="mr-auto text-[11px] text-muted-foreground">
              {index + 1} of {queue.length}
            </div>
          )}
          <Button onClick={gotIt} disabled={busy} data-testid="whatsnew-inbox-dismiss" className="rounded-full">
            {busy ? "…" : index + 1 < queue.length ? <>Next <ArrowRight className="h-3 w-3 ml-1" /></> : "Got it"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
